import { useCallback, useState } from "react";

// The guest's own choice, kept across visits. It wins over the default.
const KEY = "tempUnit";
const US_PATTERN = /\b(USA|United States)\b/i;

// Is the property in the US? Checked, in order, against the room's address
// (stored as Places JSON with address_components, or older plain text) and
// then the weather response's own location.
const isUSProperty = (room, weather) => {
  const raw = room?.address;
  if (typeof raw === "string" && raw) {
    try {
      const parsed = JSON.parse(raw);
      const country = parsed?.address_components?.find((c) =>
        c?.types?.includes("country")
      );
      if (country?.short_name) return country.short_name === "US";
      if (parsed?.formatted_address) {
        return US_PATTERN.test(parsed.formatted_address);
      }
    } catch {
      return US_PATTERN.test(raw);
    }
  }
  return US_PATTERN.test(weather?.location?.country || "");
};

const readChoice = () => {
  try {
    const value = localStorage.getItem(KEY);
    return value === "F" || value === "C" ? value : null;
  } catch {
    return null;
  }
};

/**
 * °F by default for US properties, °C everywhere else. Tapping the pill
 * calls `toggle`, which flips the unit and remembers it for next time.
 */
export const useTemperatureUnit = (room, weather) => {
  const [choice, setChoice] = useState(readChoice);
  const unit = choice || (isUSProperty(room, weather) ? "F" : "C");

  const toggle = useCallback(() => {
    const next = unit === "F" ? "C" : "F";
    setChoice(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // Storage unavailable; the choice lasts for this visit only.
    }
  }, [unit]);

  return { unit, toggle };
};

/** "72°F" / "22°C" from a weatherapi.com `current` block, or "". */
export const formatTemperature = (current, unit) => {
  const c = current?.temp_c;
  if (typeof c !== "number") return "";
  const f = typeof current?.temp_f === "number" ? current.temp_f : (c * 9) / 5 + 32;
  return unit === "F" ? `${Math.round(f)}°F` : `${Math.round(c)}°C`;
};
